import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  CircularProgress,
  Paper,
  TextField
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import weatherimage from "../assets/weather.jpg";

// Styled Components (copied from HomePage.js)
const Navbar = styled(AppBar)`
  && {
    background-color: #2e7d32 !important;
    color: white !important;
    height: 80px;
    display: flex;
    justify-content: center;
    box-shadow: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }
`;

const BackgroundWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  background: url(${weatherimage}) no-repeat center center;
  background-size: cover;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: #f5f5f5;
  padding: 20px;
`;

const WeatherContainer = styled(Paper)`
  padding: 30px 40px;
  max-width: 400px;
  width: 100%;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ToolbarContainer = styled(Toolbar)`
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  padding: 0 30px;
`;

const LogoText = styled(Typography)`
  font-weight: bold;
  font-size: 1.8rem;
  cursor: pointer;
`;

const NavButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const NavButton = styled(Button)`
  font-size: 1.1rem;
`;

const Weather = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [city, setCity] = useState("Chennai");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [recommendation, setRecommendation] = useState("");

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!isLoggedIn) {
      navigate("/");
    }
  }, [navigate]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const generateRecommendation = () => {
    if (!weatherData) return;

    const { temperature, humidity, condition } = weatherData;
    let message = "Based on current weather:\n";

    if (condition.toLowerCase().includes("rain")) {
      message += "- Rain expected: Avoid irrigation today.\n";
    } else {
      message += "- No rain: Consider irrigating if soil is dry.\n";
    }

    if (temperature > 35) {
      message += "- High temp: Use mulching or shade for crops.\n";
    } else if (temperature < 15) {
      message += "- Low temp: Use protective covers for sensitive crops.\n";
    }

    if (humidity > 85) {
      message += "- High humidity: Monitor for fungal diseases.\n";
    } else if (humidity < 30) {
      message += "- Low humidity: Irrigation may be needed more frequently.\n";
    }

    setRecommendation(message);
  };

  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn");
    navigate("/");
  };

  const fetchWeather = async () => {
    try {
      setLoading(true);
      const apiKey = "0e49f6e24071fd13cdfbe390234ce9b5";
      const response = await axios.get(
        `https://api.openweathermap.org/data/2.5/weather?q=${city}&appid=${apiKey}&units=metric`
      );

      const formattedData = {
        city: response.data.name,
        temperature: response.data.main.temp,
        humidity: response.data.main.humidity,
        condition: response.data.weather[0].description
      };

      setWeatherData(formattedData);
      setError("");
    } catch (err) {
      console.error("Error fetching weather:", err);
      setError("Failed to fetch weather data");
      setWeatherData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCityChange = (e) => {
    setCity(e.target.value);
  };

  return (
    <>
      {/* Navbar */}
      <Navbar position="fixed">
        <ToolbarContainer>
          <LogoText variant="h6" onClick={() => navigate("/home")}>
            Growsphere
          </LogoText>
          <NavButtonsContainer>
            <NavButton color="inherit" onClick={() => navigate("/home")}>
              Home
            </NavButton>
            <NavButton color="inherit" onClick={() => navigate("/disease")}>
              Disease
            </NavButton>
            <NavButton color="inherit" onClick={() => navigate("/crop")}>
              Crop
            </NavButton>
            <NavButton color="inherit" onClick={() => navigate("/fertilizer")}>
              Fertilizer
            </NavButton>
            <NavButton color="inherit" onClick={() => navigate("/weather")}>
              Weather
            </NavButton>
            <NavButton color="inherit" onClick={() => navigate("/lcc")}>
              LeafColourChart
            </NavButton>
            <IconButton color="inherit" onClick={handleMenuClick}></IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </NavButtonsContainer>
        </ToolbarContainer>
      </Navbar>

      {/* Background Wrapper */}
      <BackgroundWrapper>
        <Typography variant="h4" gutterBottom>
          Weather Information
        </Typography>
        <TextField
          label="Enter city"
          variant="outlined"
          value={city}
          onChange={handleCityChange}
          disabled={loading}
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
            width: "300px"
          }}
          InputProps={{
            style: {
              color: "#333"
            }
          }}
          InputLabelProps={{
            style: {
              color: "#666"
            }
          }}
        />
        <br />
        <Button
          variant="contained"
          onClick={fetchWeather}
          disabled={loading}
          style={{
            marginTop: "20px",
            backgroundColor: "#2e8d32",
            color: "white"
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Get Weather"}
        </Button>

        {error && (
          <Typography color="error" style={{ marginTop: "20px" }}>
            {error}
          </Typography>
        )}

        {weatherData && (
          <WeatherContainer elevation={3}>
            <Typography variant="h5">{weatherData.city}</Typography>
            <Typography>Temperature: {weatherData.temperature}°C</Typography>
            <Typography>Humidity: {weatherData.humidity}%</Typography>
            <Typography>Condition: {weatherData.condition}</Typography>
          </WeatherContainer>
        )}

        {/* Explore Recommendation Button */}
        <Button
          variant="contained"
          style={{ backgroundColor: "#2e8d32", color: "white", marginTop: "20px" }}
          onClick={generateRecommendation}
        >
          Explore Recommendation
        </Button>

        {/* Farmer Recommendation */}
        {recommendation && (
          <WeatherContainer elevation={3} style={{ marginTop: "20px" }}>
            <Typography variant="h6">Farmer Recommendation</Typography>
            <Typography style={{ whiteSpace: "pre-wrap", textAlign: "left" }}>
              {recommendation}
            </Typography>
          </WeatherContainer>
        )}
      </BackgroundWrapper>
    </>
  );
};

export default Weather;
